
import { Options, Vue } from "vue-class-component";
import DetailLayout from "@/app/ui/layout/detail-layout.vue";
import {
  dateToDateString,
  dateToTime,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import formatByCountry from "@/app/infrastructures/misc/common-library/FormatByCountry";
import Title from "@/app/ui/components/data-wrapper/title.vue";
import { TabsData } from "@/app/ui/components/tabs/module";
import { HandoverController } from "@/app/ui/controllers/HandoverController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  HandoverFailedStt,
  HandoverFailedVendorSttItem
} from "@/domain/entities/Handover";
import {
  VENDOR_ORDER_STATUS_NON_API,
  buildFailedSttColumns,
  resolveHandoverStatusBadgeHtml,
  countFailedRespondedStt,
  renderApiResponseBadgeHtml,
  isApiResponseStatusFailed,
  buildApiResponseTooltipHtml
} from "./detail-stt-status";

@Options({
  components: {
    DetailLayout,
    Title
  }
})
export default class Detail extends Vue {
  activeSttTab = "success";
  showRetryModal = false;
  retryProcessCount = 0;

  mounted() {
    const id = this.$route.params.id;
    this.getDetailHandover(id);
    HandoverController.getFailedVendorStt(Number(id));
    HandoverController.getHandoverFailedStt(Number(id));
  }

  getDetailHandover(id: any) {
    HandoverController.getDetailHandover(id);
  }

  onChangeSttTab(value: string) {
    this.activeSttTab = value;
  }

  get beforeRoute(): string {
    return (this.$route.meta?.before as string) || "/handover";
  }

  get partnerType() {
    const accountType = AccountController.accountData.account_type;
    const accountTypeDetail =
      AccountController.accountData.account_type_detail.type;
    const isConsole =
      accountTypeDetail === "console" ? "Consolidator" : "Sub Consolidator";
    return (accountType === "partner" ? isConsole : accountType) || "internal";
  }

  get id(): number {
    return Number(this.$route.params.id);
  }

  get title(): string {
    return this.$t("handover.list.detail.title", { id: this.id }) as string;
  }

  get detailData() {
    return HandoverController.detailHandover;
  }

  get isLoading() {
    return HandoverController.isLoadingDetail;
  }

  get isError() {
    return HandoverController.isError;
  }
  get errorCause() {
    return HandoverController.errorCause;
  }
  
  get showPrintVendorReceipt() {
    return this.detailData.handoverIsSupportPrintResi;
  }

  // Berhasil / Gagal handover tabs
  get successStt() {
    return this.detailData.stt;
  }

  get failedSttList() {
    return this.handoverFailedStt.failedStt;
  }

  get handoverFailedStt(): HandoverFailedStt {
    return (
      HandoverController.handoverFailedSttByHandoverId[this.id] ||
      new HandoverFailedStt()
    );
  }

  get sttTabs(): TabsData[] {
    return [
      new TabsData({
        value: "success",
        title: this.$t("handover.list.detail.successTab", {
          n: this.successStt.length
        }) as string
      }),
      new TabsData({
        value: "failed",
        title: this.$t("handover.list.detail.failedTab", {
          n: this.handoverFailedStt.totalFailed
        }) as string
      })
    ];
  }

  get isForeignAccount() {
    return !!AccountController.accountData.accountIsForeign;
  }

  get failedSttColumns() {
    return buildFailedSttColumns(this.isForeignAccount, (key) =>
      this.$t(key) as string
    );
  }

  get vendorOrderStatus(): string {
    return HandoverController.failedVendorStt.vendorOrderStatus;
  }

  // Status Respon API column (Call API vendor only)
  get showApiResponseColumn() {
    return (
      !!this.vendorOrderStatus &&
      this.vendorOrderStatus !== VENDOR_ORDER_STATUS_NON_API
    );
  }

  // Sidebar "Status Handover" badge (Berhasil / Berhasil Sebagian / Gagal).
  // Non-API vendors render no badge (as-is design, GENESIS-25373).
  get handoverStatusBadgeHtml(): string {
    return resolveHandoverStatusBadgeHtml(this.detailData.handoverStatus, {
      success: this.$t("handover.list.statusHandover.SUCCESS") as string,
      partialSuccess: this.$t(
        "handover.list.statusHandover.PARTIAL_SUCCESS"
      ) as string,
      failed: this.$t("handover.list.statusHandover.FAILED") as string
    });
  }

  get failedRespondedSttCount(): number {
    return countFailedRespondedStt(HandoverController.failedVendorStt.stt);
  }

  get showRetryButton(): boolean {
    return (
      this.vendorOrderStatus !== VENDOR_ORDER_STATUS_NON_API &&
      HandoverController.failedVendorStt.isRetryAvailable &&
      this.failedRespondedSttCount > 0
    );
  }

  get failedVendorSttMap() {
    const map = new Map<string, HandoverFailedVendorSttItem>();
    HandoverController.failedVendorStt.stt.forEach((item) => {
      map.set(item.sttNo, item);
    });
    return map;
  }

  // table
  get columns() {
    const baseColumns: any[] = [
      {
        name: this.$t("handover.detail.table.no"),
        styleHead: "w-10 text-left whitespace-nowrap",
        render: (_: any, i: number) =>
          `<span class="font-normal text-black-lp-300 whitespace-nowrap">${i +
            1}</span>`
      },
      {
        name: this.$t("handover.detail.table.stt"),
        styleHead: "w-56 text-left whitespace-nowrap",
        render: (item: any) => {
          return `<div class="text-black-lp-300 whitespace-nowrap">
                  ${item.sttNo}
                </div>`;
        }
      },
      {
        name: this.$t("handover.detail.table.product"),
        styleHead: "w-56 text-left whitespace-nowrap",
        render: (item: any) => {
          return `<div class="text-black-lp-300 flex">
                  <div class="rounded px-2 py-0 bg-gray-lp-400">
                      ${item.sttProductType}
                  </div>
                </div>`;
        }
      },
      {
        name: this.$t("handover.detail.table.commodityName"),
        styleHead: "w-72 text-left whitespace-nowrap",
        styleCustom: "w-72 text-left",
        render: (item: any) =>
          `<div class="font-normal text-black-lp-300 break-words pr-2">${item.sttCommodityName}</div>`
      },
      {
        name: this.$t("handover.detail.table.pieces"),
        styleHead: "w-48 text-left whitespace-nowrap",
        styleCustom: "justify-center",
        render: (item: any) =>
          `<span class="font-normal text-black-lp-300 whitespace-nowrap">${item.sttTotalPiece}</span>`
      },
      {
        name: this.$t("handover.detail.table.grossWeight"),
        styleHead: "w-64 text-left whitespace-nowrap",
        render: (item: any) =>
          `<span class="font-normal text-black-lp-300 whitespace-nowrap">${this.formatWeight(item.sttGrossWeight)}</span>`
      },
      {
        name: this.$t("handover.detail.table.volumeWeight"),
        styleHead: "w-64 text-left whitespace-nowrap",
        render: (item: any) =>
          `<span class="font-normal text-black-lp-300 whitespace-nowrap">${this.formatWeight(item.sttVolumeWeight)}</span>`
      },
      {
        name: this.$t("handover.detail.table.origin"),
        styleHead: "w-56 text-left whitespace-nowrap",
        render: (item: any) => {
          return `<div class="text-black-lp-300 whitespace-nowrap">
                  ${item.sttOriginCityId} - ${item.sttOriginCityName}
                </div>`;
        }
      },
      {
        name: this.$t("handover.detail.table.dest"),
        styleHead: "w-56 text-left whitespace-nowrap",
        render: (item: any) => {
          return `<div class="text-black-lp-300 whitespace-nowrap">
                  ${item.sttDestinationCityId} - ${item.sttDestinationCityName}
                </div>`;
        }
      }
    ];

    if (this.showApiResponseColumn) {
      baseColumns.push({
        name: this.$t("handover.list.table.statusHitApi"),
        styleHead: "w-56 text-left whitespace-nowrap",
        "custom-slot": true,
        slotName: "statusHitApi"
      });
    }

    return baseColumns;
  }

  renderApiStatusBadge(item: any): string {
    const match = this.failedVendorSttMap.get(item?.sttNo);
    return renderApiResponseBadgeHtml(match?.status || "-", {
      success: this.$t("handover.list.statusHitApi.SUCCESS") as string,
      failed: this.$t("handover.list.statusHitApi.FAILED") as string,
      reason: this.$t("handover.list.detail.reason") as string,
      viewReason: this.$t("handover.list.detail.viewReason") as string
    });
  }

  isApiStatusFailed(item: any): boolean {
    const match = this.failedVendorSttMap.get(item?.sttNo);
    return isApiResponseStatusFailed(match?.status || "");
  }

  renderApiStatusTooltip(item: any): string {
    const match = this.failedVendorSttMap.get(item?.sttNo);
    const reason = match?.reason || match?.message || "-";
    const reasonEn = match?.reasonEn || match?.message || "-";
    return buildApiResponseTooltipHtml(
      this.isForeignAccount ? reasonEn : reason,
      this.$t("handover.list.detail.reason") as string
    );
  }

  pagination = {
    page: 1,
    limit: 100
  };

  goBack() {
    this.$router.push(this.beforeRoute);
  }

  onClickRetry() {
    const eligibleSttNos = HandoverController.failedVendorStt.stt
      .filter((item) => item.retryAvailable)
      .map((item) => item.sttNo);

    HandoverController.retryHandoverVendorOrder({
      handoverId: this.id,
      sttNo: eligibleSttNos[0] || "",
      sttNos: eligibleSttNos
    })
      .then(() => {
        this.retryProcessCount = eligibleSttNos.length;
        this.showRetryModal = true;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            err,
            this.$t("handover.list.retry.failedToast"),
            () => this.onClickRetry()
          )
        );
      });
  }

  closeRetryModal() {
    this.showRetryModal = false;
  }

  formatDate(date: string) {
    return `${dateToDateString(date)}, ${dateToTime(date)} WIB`;
  }

  formatWeight(value: number): string {
    return formatByCountry(value, { type: "weight", suffix: " Kg" });
  }

  // Print

  async print(type: string) {
    const book: any = await import("../modules/print.vue");
    const printType: any = {
      summary: () => book.default.methods.printSummary(this.roleAccount),
      basic: () => book.default.methods.print(this.roleAccount)
    };

    printType[type]();
  }

  async printVendorReceipt() {
    const handoverId = this.$route.params.id;

    const book: any = await import("../modules/print.vue");
    await book.default.methods.printVendorReceipt(handoverId, this.detailData.handoverVendorCode);
  }

  // role account users
  get roleAccount() {
    return (
      (AccountController.accountData.account_type === "partner"
        ? AccountController.accountData.account_type_detail.type
        : AccountController.accountData.account_type) || "internal"
    );
  }
}
